'use client';

import { ForbiddenScreen } from '@modules/shared/components/status-screens/forbidden-screen/forbidden-screen';
import { AbsoluteSpinner } from 'app/_components/AbsoluteSpinner';
import { useGlobalAccess } from 'app/_providers/GlobalAccessProvider';

import * as Sentry from '@sentry/nextjs';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState, useRef } from 'react';
import { MemberAccessCheckRequestItem, MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';

interface PermissionCache {
  organization?: Record<string, boolean>;
  workspace?: Record<string, boolean>;
  product?: Record<string, boolean>;
  system?: Record<string, boolean>;
}

interface AccessCheckLoaderProps {
  children: React.ReactNode;
  accessChecks: MemberAccessCheckRequestItem[];
  // Shown while permissions are loading.
  fallback?: React.ReactElement;
  // Optional. Shown when user lacks required permissions. If omitted, fallback is used.
  accessDeniedFallback?: React.ReactElement;
}

export const AccessCheckLoader = ({
  children,
  accessChecks,
  fallback = <AbsoluteSpinner />,
  accessDeniedFallback,
}: AccessCheckLoaderProps): React.ReactElement => {
  const { getPermissions, requestPermissions } = useGlobalAccess();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prevChecks = useRef(accessChecks);
  const prevCached = useRef<PermissionCache | null>(null);
  const prevRequested = useRef(false);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setLoading(true);
        setError(null);
        prevRequested.current = false; // Reset for new effect run

        // Track access checks changes
        if (!isEqual(prevChecks.current, accessChecks)) {
          prevChecks.current = accessChecks;
        }

        // Check if already cached
        const cached = getPermissions(accessChecks);

        // Track cache changes
        if (!isEqual(prevCached.current, cached)) {
          prevCached.current = cached;
        }

        if (cached) {
          // Permissions are already cached, no need to load
          return;
        }

        // Always request permissions - the provider will handle deduplication
        if (!prevRequested.current) {
          prevRequested.current = true;
        }
        await requestPermissions(accessChecks);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load permissions';
        setError(errorMessage);

        // Log to Sentry for non-unauthorized errors
        if (!errorMessage.includes('Unauthorized')) {
          Sentry.captureException(err as Error, {
            tags: {
              component: 'AccessCheckLoader',
              action: 'loadPermissions',
            },
            extra: {
              accessChecks,
              errorMessage,
            },
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [accessChecks, getPermissions, requestPermissions]);

  if (loading) {
    return fallback;
  }

  if (error) {
    return accessDeniedFallback ?? <ForbiddenScreen />;
  }

  // Check if user has the required permissions
  const permissions = getPermissions(accessChecks);

  if (!permissions) {
    // Permissions not loaded yet (shouldn't happen since loading is false, but be safe)
    return fallback;
  }

  const hasRequiredAccess = accessChecks.every((check) => {
    if (!check.checks || check.checks.length === 0) {
      return true; // No specific permissions required
    }

    // For each access check, user needs at least ONE of the specified permissions
    return check.checks.some((permission) => {
      // Check system permissions
      if (check.accessType === MemberAccessCheckType.System) {
        const systemPermissions = permissions.system || {};

        return systemPermissions[permission] === true;
      }

      // Check organization permissions
      if (check.accessType === MemberAccessCheckType.Organization) {
        const orgPermissions = permissions.organization || {};

        return orgPermissions[permission] === true;
      }

      // Check workspace permissions
      if (check.accessType === MemberAccessCheckType.Workspace) {
        const workspacePermissions = permissions.workspace || {};

        return workspacePermissions[permission] === true;
      }

      // Check product permissions
      if (check.accessType === MemberAccessCheckType.Product) {
        const productPermissions = permissions.product || {};

        return productPermissions[permission] === true;
      }

      return false;
    });
  });

  if (!hasRequiredAccess) {
    // User doesn't have required permissions
    return accessDeniedFallback ?? fallback;
  }

  return children as React.ReactElement;
};
